/**
 * Experience gift-card selection logic.
 *
 * Pure and client-safe: the amount page uses these to render the live price and
 * to serialize the selection; the action re-derives the same values server-side.
 *
 * Offers/pricing authority: the *display* price and the item list are derived
 * here straight from each variant's VAT-inclusive unit price (mirroring the
 * legacy `getPriceBasedOnExperience`). Signed offer tokens are NOT produced here
 * — they're attached at purchase time from `computePriceSummary` (see the
 * checkout step), exactly like the booking flow. So `BuiltGiftCardItem` carries
 * no `offer`; the basket stores variant id + quantity and the purchase step
 * enriches it.
 */

/** A selectable add-on under a variant. Same id/name/price shape as a variant —
 *  add-ons are priced as their own line items (with signed add-on offers) at
 *  purchase time, exactly like the booking flow. */
export type GiftCardAddOnOption = {
  id: string;
  name: string;
  /** VAT-inclusive unit price in minor units (cents). */
  priceCents: number;
};

export type GiftCardVariantOption = {
  id: string;
  name: string;
  /** VAT-inclusive unit price in minor units (cents). */
  priceCents: number;
  /** Paid add-ons selectable once the variant itself has a quantity. */
  addOns?: GiftCardAddOnOption[];
};

export type GiftCardExperienceOption = {
  id: string;
  name: string;
  currency: string;
  variants: GiftCardVariantOption[];
};

/** Selected quantity per variant/add-on id. Zero-quantity entries are excluded. */
export type GiftCardGuests = Record<string, number>;

export type BuiltGiftCardItem = {
  itemId: string;
  name: string;
  quantity: number;
};

export function totalGuestCount(guests: GiftCardGuests): number {
  return Object.values(guests).reduce((sum, qty) => sum + qty, 0);
}

/** Flatten an experience's variants and their add-ons into an id→option map, so
 *  a `guests` selection (keyed by either kind of id) can be priced and named in
 *  one lookup. */
function priceableById(
  experience: GiftCardExperienceOption
): Map<string, { name: string; priceCents: number }> {
  const map = new Map<string, { name: string; priceCents: number }>();
  for (const variant of experience.variants) {
    map.set(variant.id, { name: variant.name, priceCents: variant.priceCents });
    for (const addOn of variant.addOns ?? []) {
      map.set(addOn.id, { name: addOn.name, priceCents: addOn.priceCents });
    }
  }
  return map;
}

/** Display price (minor units) = Σ selected variant/add-on unit price × quantity. */
export function deriveExperiencePriceCents(
  experience: GiftCardExperienceOption,
  guests: GiftCardGuests
): number {
  const byId = priceableById(experience);
  return Object.entries(guests).reduce((total, [id, qty]) => {
    const option = byId.get(id);
    return option ? total + option.priceCents * qty : total;
  }, 0);
}

/** Build the basket item list (variant/add-on id + name + quantity) for a
 *  selection, dropping zero and unknown ids. */
export function buildGiftCardItems(
  experience: GiftCardExperienceOption,
  guests: GiftCardGuests
): BuiltGiftCardItem[] {
  const byId = priceableById(experience);
  return Object.entries(guests)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const option = byId.get(id);
      return option ? { itemId: id, name: option.name, quantity: qty } : null;
    })
    .filter((item): item is BuiltGiftCardItem => item !== null);
}
